import React, { useRef, useState } from 'react';
import { Button, FormFeedback, Input, Label, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Recaptcha from '../../Components/Common/Recaptcha';
import PasswordStrengthChecklist from '../../Components/Common/PasswordStrengthChecklist';
import { isPasswordStrong } from '../../constants/passwordPolicy';
import { changeExpiredPasswordThunk } from '../../slices/thunks';

/**
 * OP-014: Force password change on login when the current password is expired.
 * Rendered inline in Login.js as the first branch (before the 2FA branch).
 *
 * `location` is threaded through from Login.js so the BFF can build the same
 * DeviceMetadataDto shape used by the login flow — keeps OP-013 device tracking
 * consistent across auth entry points.
 */
const ForcePasswordChangeStep = ({ passwordChangeToken, location, onSuccess, t }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [passwordConfShow, setPasswordConfShow] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const formik = useFormik({
    initialValues: { newPassword: '', confirmation: '' },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required(t('password-recovery.required'))
        .test('is-strong-password', t('validation-weak-password'), (value) => isPasswordStrong(value)),
      confirmation: Yup.string()
        .required(t('password-recovery.required'))
        .oneOf([Yup.ref('newPassword'), null], t('password-recovery.confirmation.invalid')),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const result = await dispatch(
        changeExpiredPasswordThunk(passwordChangeToken, values.newPassword, recaptchaToken, location),
      );
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsLoading(false);

      if (result?.error) return;
      // pending2fa: the 2FA reducer branch will already have been dispatched by the thunk;
      // Login.js will re-render into <TwoFactorCodeStep />. No further work here.
      if (result?.pending2fa) return;
      // Success (logged in): forward the subjects to the parent redirect helper.
      onSuccess(result);
    },
  });

  const hasNewPassError = !!(formik.touched.newPassword && formik.errors.newPassword);
  const hasConfError = !!(formik.touched.confirmation && formik.errors.confirmation);

  return (
    <div className="p-2 mt-4">
      <div className="text-center mb-3">
        <h5 className="text-primary">{t('password-expired.heading')}</h5>
        <p className="text-muted">{t('password-expired.description')}</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
          return false;
        }}
      >
        <div className="mb-3">
          <Label className="form-label">{t('password-recovery.modal.new-password')}</Label>
          <div className="position-relative auth-pass-inputgroup">
            <Input
              name="newPassword"
              type={passwordShow ? 'text' : 'password'}
              className="form-control pe-5"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.newPassword || ''}
              invalid={hasNewPassError}
            />
            {hasNewPassError && <FormFeedback type="invalid">{formik.errors.newPassword}</FormFeedback>}
            {formik.values.newPassword ? (
              <button
                className="btn btn-link position-absolute top-0 text-decoration-none text-muted"
                type="button"
                onClick={() => setPasswordShow(!passwordShow)}
                style={{ right: hasNewPassError ? 20 : 0 }}
              >
                <i className="ri-eye-fill align-middle"></i>
              </button>
            ) : null}
          </div>
          <PasswordStrengthChecklist password={formik.values.newPassword} t={t} />
        </div>

        <div className="mb-3">
          <Label className="form-label">{t('password-recovery.modal.new-password-confirmation')}</Label>
          <div className="position-relative auth-pass-inputgroup">
            <Input
              name="confirmation"
              type={passwordConfShow ? 'text' : 'password'}
              className="form-control pe-5"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmation || ''}
              invalid={hasConfError}
            />
            {hasConfError && <FormFeedback type="invalid">{formik.errors.confirmation}</FormFeedback>}
            {formik.values.confirmation ? (
              <button
                className="btn btn-link position-absolute top-0 text-decoration-none text-muted"
                type="button"
                onClick={() => setPasswordConfShow(!passwordConfShow)}
                style={{ right: hasConfError ? 20 : 0 }}
              >
                <i className="ri-eye-fill align-middle"></i>
              </button>
            ) : null}
          </div>
        </div>

        <Recaptcha ref={recaptchaRef} onChange={setRecaptchaToken} />

        <div className="mt-4">
          <Button
            color="success"
            className="w-100"
            type="submit"
            disabled={isLoading || !recaptchaToken || !formik.isValid}
          >
            {isLoading && <Spinner size="sm" className="me-2" />}
            {t('password-expired.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ForcePasswordChangeStep;
